import React, { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { agentService } from '../../services/agentService';
import { isAdmin } from '../../services/auth';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';

const currencyMap = {
    'None': '', 'USD': '$', 'GBP': '£', 'BTC': '₿', 'EUR': '€', 'CHF': '₣',
    'KRW': '₩', 'TRY': '₺', 'INR': '₹', 'CNY': '¥', 'THB': '฿',
    'RUB': '₽', 'UAH': '₴', 'BDT': '৳', 'JPY': '¥', 'ILS': '₪',
    'AED': 'د.إ', 'SAR': 'ر.س'
};

const UserAdjustmentModal = ({ show, onHide, user, onSuccess }) => {
    const { t } = useTranslation();
    const { settings } = useTheme();
    const [loading, setLoading] = useState(false);
    const [amount, setAmount] = useState('');
    const [type, setType] = useState('add');
    const [currency, setCurrency] = useState(user?.currency || 'None');

    const currentSymbol = (currency && currencyMap[currency] !== undefined) ? currencyMap[currency] : (currency === 'None' ? '' : '$');
    const userSymbol = (user.currency && currencyMap[user.currency] !== undefined) ? currencyMap[user.currency] : (user.currency === 'None' ? '' : '$');

    if (!user) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validAmount = amount && !isNaN(amount) && parseFloat(amount) > 0;

        if (!validAmount && !currency) {
            toast.error(t('msg_amount_or_currency_required', 'Please provide an amount or change the currency'));
            return;
        }

        setLoading(true);
        try {
            const response = await agentService.adjustBalance(user.id, {
                amount: validAmount ? parseFloat(amount) : undefined,
                type,
                currency: isAdmin() ? currency : undefined
            });

            if (response.success) {
                toast.success(t('msg_balance_adj_success', { type: type === 'add' ? t('added') : t('deducted') }));
                setAmount('');
                if (onSuccess) onSuccess();
                onHide();
            }
        } catch (error) {
            const msg = error.message ? t(error.message) : t('error_adjust_balance');
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            scrollable
            contentClassName="majestic-modal-glass border-0 shadow-lg"
            data-bs-theme={settings.darkMode ? 'dark' : 'light'}
        >
            <div className="majestic-modal-content">
                <Modal.Header closeButton className="border-bottom border-light p-4" closeVariant="white">
                    <Modal.Title className="fs-6 fw-bold text-white d-flex align-items-center gap-2">
                        <i className="bi bi-wallet2 majestic-text-gold"></i>
                        {t('balance_adjustment', 'Balance Adjustment')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <div className="majestic-balance-card mb-4">
                        <div className="d-flex flex-column">
                            <span className="majestic-label-text opacity-50 mb-1">{t('current_balance')}</span>
                            <span className="fw-bold fs-3 majestic-text-gold">
                                {userSymbol}{user.accountBalance?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                        </div>
                        <div className="majestic-label-dot"></div>
                    </div>

                    <Form onSubmit={handleSubmit}>
                        <div className="majestic-form-group">
                            <div className="majestic-label-row">
                                <div className="majestic-label-dot"></div>
                                <span className="majestic-label-text">{t('adjustment_type', 'Adjustment Type')}</span>
                            </div>
                            <div className="d-flex gap-3">
                                <div
                                    className={`majestic-radio-option add ${type === 'add' ? 'active' : ''}`}
                                    onClick={() => setType('add')}
                                >
                                    <i className="bi bi-plus-circle"></i>
                                    {t('add', 'Add')}
                                </div>
                                <div
                                    className={`majestic-radio-option subtract ${type === 'subtract' ? 'active' : ''}`}
                                    onClick={() => setType('subtract')}
                                >
                                    <i className="bi bi-dash-circle"></i>
                                    {t('deduct', 'Deduct')}
                                </div>
                            </div>
                        </div>

                        <div className="majestic-form-group">
                            <div className="majestic-label-row">
                                <div className="majestic-label-dot"></div>
                                <label htmlFor="amount" className="majestic-label-text">{t('amount')}</label>
                            </div>
                            <div className="majestic-input-wrapper">
                                <div className="position-absolute h-100 d-flex align-items-center ps-3 text-secondary opacity-50" style={{ zIndex: 5 }}>
                                    {currentSymbol}
                                </div>
                                <Form.Control
                                    id="amount"
                                    name="amount"
                                    type="number"
                                    placeholder="0.00"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    className="majestic-input-field"
                                    style={{ paddingLeft: currentSymbol ? '2.5rem' : '1rem' }}
                                />
                            </div>
                        </div>

                        {/* Currency selector for Admins */}
                        {isAdmin() && (
                            <div className="majestic-form-group mt-3">
                                <div className="majestic-label-row">
                                    <div className="majestic-label-dot"></div>
                                    <span className="majestic-label-text">{t('currency', 'Currency')}</span>
                                </div>
                                <div className="majestic-currency-picker">
                                    {[
                                        { code: 'None', label: 'None' },
                                        { code: 'USD', label: 'Dollar ($)' },
                                        { code: 'GBP', label: 'Pound (£)' },
                                        { code: 'BTC', label: 'Bitcoin (₿)' },
                                        { code: 'EUR', label: 'Euro (€)' },
                                        { code: 'CHF', label: 'Franc (₣)' },
                                        { code: 'KRW', label: 'Won (₩)' },
                                        { code: 'TRY', label: 'Lira (₺)' },
                                        { code: 'INR', label: 'Rupee (₹)' },
                                        { code: 'CNY', label: 'Yuan (¥)' },
                                        { code: 'THB', label: 'Baht (฿)' },
                                        { code: 'RUB', label: 'Ruble (₽)' },
                                        { code: 'UAH', label: 'Hryvnia (₴)' },
                                        { code: 'BDT', label: 'Taka (৳)' },
                                        { code: 'JPY', label: 'Yen (¥)' },
                                        { code: 'ILS', label: 'Shekel (₪)' },
                                        { code: 'AED', label: 'UAE Dirham (د.إ)' },
                                        { code: 'SAR', label: 'Saudi Riyal (ر.س)' }
                                    ].map(c => (
                                        <div
                                            key={c.code}
                                            className={`majestic-currency-option ${currency === c.code ? 'active' : ''}`}
                                            onClick={() => setCurrency(c.code)}
                                        >
                                            {c.label}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="d-flex justify-content-end gap-3 mt-5 pt-3 border-top border-light modal-footer-btns">
                            <Button
                                variant="outline-light"
                                size="sm"
                                onClick={onHide}
                                className="px-4 py-2 border-0 opacity-50"
                                style={{ borderRadius: '12px' }}
                            >
                                {t('cancel')}
                            </Button>
                            <Button
                                variant={type === 'add' ? 'success' : 'danger'}
                                size="lg"
                                type="submit"
                                disabled={loading}
                                className="px-5 py-2 fw-bold"
                                style={{ borderRadius: '12px', minWidth: '160px' }}
                            >
                                {loading ? (
                                    <><span className="spinner-border spinner-border-sm me-2"></span>{t('processing')}</>
                                ) : (
                                    <>{t('confirm')} <i className="bi bi-check2-circle ms-2"></i></>
                                )}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </div>
        </Modal>
    );
};

export default UserAdjustmentModal;
